import { BrowserModule } from '@angular/platform-browser';
import { XhrFactory } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, Injectable, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi, HttpErrorResponse, HTTP_INTERCEPTORS, FetchBackend } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { IMaskModule } from 'angular-imask';

import { AppComponent } from './app.component';
import { BuyWithin90Component } from './buy-within-90/buy-within-90.component';
import { BuyWithin90FormComponent } from './buy-within-90/buy-within-90-form/buy-within-90-form.component';
import { EmailVerificationComponent } from './email-verification/email-verification.component';
import { OneToOneConsentComponent } from './one-to-one-consent/one-to-one-consent.component';
import { BrandService } from '../../../services/brand.service';
import { warmAdvertisedRate } from './advertised-rate-warmup';
import { FruHttpService } from '../../../services/fru-http.service';
import { XhrFetchFallbackInterceptor, XhrFetchFallbackXhrFactory } from '../../../services/xhr-fetch-fallback.interceptor';
import { AppRoutingModule } from './/app-routing.module';
import { LeadFormComponent } from './email-verification/lead-form/lead-form.component';
import { ConsentFormComponent } from './one-to-one-consent/consent-form/consent-form.component';
import { FormLandingComponent } from './form-landing/form-landing.component';
import { LenderListingComponent } from './form-landing/fru/lender-listings/lender-listing/lender-listing.component';
import { LenderListingsComponent } from './form-landing/fru/lender-listings/lender-listings.component';
import { FormLandingHeaderComponent } from './form-landing/fru/form-landing-header/form-landing-header.component';
import { FormLandingHeaderDirective } from './form-landing/form-landing-header.directive';
import { FormLandingFooterComponent } from './form-landing/fru/form-landing-footer/form-landing-footer.component';
import { FormLandingFooterDirective } from './form-landing/form-landing-footer.directive';
import { FormLandingBodyComponent } from './form-landing/fru/form-landing-body/form-landing-body.component';
import { FormLandingBodyDirective } from './form-landing/form-landing-body.directive';
import { TrackingCodeComponent } from './form-landing/tracking-code/tracking-code.component';
import { MMLandingBodyComponent } from './form-landing/mm/mmlanding-body/mmlanding-body.component';
import { MMLandingFooterComponent } from './form-landing/mm/mmlanding-footer/mmlanding-footer.component';
import { MMLandingHeaderComponent } from './form-landing/mm/mmlanding-header/mmlanding-header.component';
import { BestLandingBodyComponent } from './form-landing/best/best-landing-body/best-landing-body.component';
import { BestLandingFooterComponent } from './form-landing/best/best-landing-footer/best-landing-footer.component';
import { BestLandingHeaderComponent } from './form-landing/best/best-landing-header/best-landing-header.component';
import { CookieService } from 'ngx-cookie-service';
import { ScriptTagComponent } from '../../../shared/script-tag/script-tag.component';
import { hopeJsErrorAllowed } from '../../../shared/hope-js-error-cap';
import { RatesFormComponent } from './rates-form/rates-form.component';
import { StepComponent } from './rates-form/step/step.component';
import { PropertyTypeComponent } from './rates-form/steps/property-type/property-type.component';
import { DscrPropertyUseComponent, PropertyUseComponent } from './rates-form/steps/property-use/property-use.component';
import { ReverseAgeComponent } from './rates-form/steps/reverse-age/reverse-age.component';
import { ReverseBalanceComponent } from './rates-form/steps/reverse-balance/reverse-balance.component';
import { ReverseResidenceComponent } from './rates-form/steps/reverse-residence/reverse-residence.component';
import { ButtonStepComponent } from './rates-form/step/button-step/button-step.component';
import { BankruptcyComponent } from './rates-form/steps/bankruptcy/bankruptcy.component';
import { BankruptcyRecencyComponent } from './rates-form/steps/bankruptcy-recency/bankruptcy-recency.component';
import { EmploymentComponent } from './rates-form/steps/employment/employment.component';
import { LoanReasonComponent } from './rates-form/steps/loan-reason/loan-reason.component';
import { CashOutComponent } from './rates-form/steps/cash-out/cash-out.component';
import { CashOutReasonComponent } from './rates-form/steps/cash-out-reason/cash-out-reason.component';
import { SliderStepComponent } from './rates-form/step/slider-step/slider-step.component';
import { PropertyValueComponent } from './rates-form/steps/property-value/property-value.component';
import { FirstMortgageBalanceComponent } from './rates-form/steps/first-mortgage-balance/first-mortgage-balance.component';
import { CurrentFHAComponent } from './rates-form/steps/current-fha/current-fha.component';
import { CreditRatingComponent } from './rates-form/steps/credit-rating/credit-rating.component';
import { MultiInputStepComponent } from './rates-form/step/multi-input-step/multi-input-step.component';
import { SelectStepComponent } from './rates-form/step/select-step/select-step.component';
import { ZipCodeStreetAddressComponent } from './rates-form/steps/zip-code-street-address/zip-code-street-address.component';
import { VeteranMilitaryComponent } from './rates-form/steps/veteran-military/veteran-military.component';
import { CurrentVAComponent } from './rates-form/steps/current-va/current-va.component';
import { FirstNameLastNameComponent } from './rates-form/steps/first-name-last-name/first-name-last-name.component';
import { InputStepComponent } from './rates-form/step/input-step/input-step.component';
import { EmailComponent } from './rates-form/steps/email/email.component';
import { PhoneComponent } from './rates-form/steps/phone/phone.component';
import { NewHomeValueComponent } from './rates-form/steps/new-home-value/new-home-value.component';
import { DscrEstimatedDownPaymentComponent, EstimatedDownPaymentComponent } from './rates-form/steps/estimated-down-payment/estimated-down-payment.component';
import { ZipCodeComponent } from './rates-form/steps/zip-code/zip-code.component';
import { SliderLabelComponent } from './rates-form/step/slider-step/slider-label/slider-label.component';
import { FormFlowShellComponent } from './form-flow-shell/form-flow-shell.component';
import { BuyingProcessComponent } from './rates-form/steps/buying-process/buying-process.component';
import { FirstTimeBuyerComponent } from './rates-form/steps/first-time-buyer/first-time-buyer.component';
import { DaysFromBuyingComponent } from './rates-form/steps/days-from-buying/days-from-buying.component';
import { FoundHomeComponent } from './rates-form/steps/found-home/found-home.component';
import { SignedAgreementComponent } from './rates-form/steps/signed-agreement/signed-agreement.component';
import * as Sentry from '@sentry/browser';
import { PremiumQuestionsComponent } from './premium-questions/premium-questions.component';
import { PremiumLeadFormComponent } from './premium-questions/premium-lead-form/premium-lead-form.component';
import { BestService } from '../../../services/best.service';
import { MaximumHomeEquityComponent } from './rates-form/steps/maximum-home-equity/maximum-home-equity.component';
import { LoanPurposeComponent } from './rates-form/steps/loan-purpose/loan-purpose.component';
import { NeustarComponent  } from './rates-form/steps/neustar/neustar.component';
import { ErrorSrcDirective } from '../../../shared/error-src.directive';
import { CustomerReviewComponent } from './form-landing/customer-review/customer-review.component';
import { DigitOnlyDirective } from './directives/digit-only.directive';
import { AutofillSyncDirective } from './directives/autofill-sync.directive';
import { SliderProgressDirective } from './directives/slider-progress.directive';
import { APP_BASE_HREF } from '@angular/common';
import { TimeFrameComponent } from './rates-form/steps/time-frame/time-frame.component';
import { PropertyStatusComponent } from './rates-form/steps/property-status/property-status.component';
import { LinkClickModalComponent } from './rates-form/link-click-modal/link-click-modal.component';
import { CommunicationService } from '../../../services/communication.service';
import { SlideEntryComponent } from './form-landing/customer-review/slide-entry/slide-entry.component';
import { CustomStarRatingComponent } from './form-landing/customer-review/custom-star-rating/custom-star-rating.component';
import { StatesDropdownComponent } from '../../../shared/states-dropdown/states-dropdown.component';
import { PropertyTypeConfirmationComponent } from './rates-form/steps/property-type-confirmation/property-type-confirmation.component';
import { ProgressBarComponent } from './rates-form/progress-bar/progress-bar.component';
import { StateRateInfoComponent } from './form-landing/state-rate-info/state-rate-info.component';
import { TrustedPartnersComponent } from './form-landing/trusted-partners/trusted-partners.component';
import { TrustedPartnerComponent } from './form-landing/trusted-partner/trusted-partner.component';
import { ProductOptionsComponent } from './form-landing/product-options/product-options/product-options.component';
import { ProductInfoComponent } from './form-landing/product-options/product-info/product-info.component';
import { ProductOptionComponent } from './form-landing/product-options/product-option/product-option.component';
import { HaveExistingMortgageComponent } from './rates-form/steps/have-existing-mortgage/have-existing-mortgage.component';
import { LendersCountComponent } from './rates-form/steps/lenders-count/lenders-count.component';
import {
  annotateRateFinderSentryEvent,
  primeRateFinderSentryContext,
} from '../../../services/rate-finder-sentry';
import { hopeBootOutcomeSinks, hopeBootStallStage, watchHopeBoot } from './assistant/hope-boot-outcome';
import { notOurError } from '../../../services/sentry-noise';


// Disable sentry on development domains
const match = /(.+)\-www\.(.+)/.test(window.location.hostname);
const sentryEnabled = !match;
// FRU-22743: once per stall STAGE (why|action), not once per document, so a later 15-second or import-failure stage is not
// hidden behind an earlier 8-second wait.
const hopeBootStallStagesCaptured: string[] = [];

function captureHopeBootStall(detail?: any): void {
  if (!sentryEnabled) {
    return;
  }

  const stallDetail = detail || (window as any).__hopeBootStalled;
  if (!stallDetail) {
    return;
  }

  const stage = hopeBootStallStage(stallDetail);
  if (hopeBootStallStagesCaptured.indexOf(stage) >= 0) {
    return;
  }
  hopeBootStallStagesCaptured.push(stage);
  // FRU-24088: a stall is a timing (Matomo counts every stall stage per arm), not an error; a boot that fails outright is
  // reported by its outcome. Kept as a breadcrumb so a later error carries it.
  Sentry.addBreadcrumb({
    category: 'hope_boot',
    message: 'Hope boot stalled',
    level: 'info',
    data: { reason: stallDetail.why || 'timeout', action: stallDetail.action || 'unknown', stage },
  });
}

// FRU-22743: what each Hope boot ended as. Matomo gets one outcome event per boot, so outcomes can be read against the Hope arm
// (dimension 4) and submitted leads; Sentry gets a searchable event only for slow, fallback or unpainted boots.
function watchHopeBootOutcome(): void {
  try {
    const sentry = sentryEnabled ? {
      captureMessage: (message: string, context: any) => Sentry.captureMessage(message, context),
      addBreadcrumb: (breadcrumb: any) => Sentry.addBreadcrumb(breadcrumb),
    } : null;
    watchHopeBoot(window, hopeBootOutcomeSinks(window, sentry));
  } catch (e) { /* telemetry is non-fatal */ }
}

const trackerBlockedHosts = [
  'api.trustedform.com',
  'cert.trustedform.com',
  'bat.bing.com',
  'connect.facebook.net',
  'create.leadid.com',
  'ab.freerateupdate.com',
  'info.leadid.com',
];

function getProbableTrackerBlockerDetails(event, hint): { resource: string, host: string } | null {
  let text = '';
  try {
    text = JSON.stringify({
      message: event && event.message,
      exception: event && event.exception,
      breadcrumbs: event && event.breadcrumbs,
      originalException: hint && hint.originalException ? String(hint.originalException) : null,
    });
  } catch (e) {
    text = String(event && event.message || '') + ' ' + String(hint && hint.originalException || '');
  }

  if (text.indexOf('TrustedForm Script failed to load') !== -1) {
    return { resource: 'trustedform', host: 'api.trustedform.com' };
  }

  if (!/(failed to load|failed to fetch|load failed|blocked by client|err_blocked_by_client|networkerror|net::err)/i.test(text)) {
    return null;
  }

  const blockedHost = trackerBlockedHosts.find((host) => text.indexOf(host) !== -1);
  if (!blockedHost) {
    return null;
  }

  return {
    resource: blockedHost.indexOf('trustedform.com') !== -1 ? 'trustedform' : 'third_party_tracker',
    host: blockedHost,
  };
}

function annotateProbableTrackerBlocker(event, details: { resource: string, host: string }) {
  event.tags = {
    ...(event.tags || {}),
    probable_cause: 'tracker_blocker',
    blocked_resource: details.resource,
    blocked_host: details.host,
  };

  event.contexts = {
    ...(event.contexts || {}),
    tracker_blocker: {
      probable: true,
      reason: 'Known tracking/certification script failed to load. This is commonly caused by ad/tracker blockers such as uBlock Origin.',
      host: details.host,
      resource: details.resource,
    },
  };

  return event;
}

if(sentryEnabled) {
  primeRateFinderSentryContext('form');
  Sentry.init({
    dsn: 'https://97e2d22d850e434f9ac90e6b9ccc8419@sentry.io/1363241',
    beforeSend(event, hint) {
      // FRU-24088: an in-app browser's own script, or a crawler or unsupported engine, isn't an error of ours.
      if (notOurError(event, typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
        return null;
      }
      const err = hint && hint.originalException;
      // FRU-22871: consent submit (/selected_manager/onetoone) joins lead submit in the
      // allowlist so consent-flow HttpErrorResponses stay observable; all other
      // HttpErrorResponse events are still dropped.
      const isLeadSubmitError = err instanceof HttpErrorResponse
        && typeof err.url === 'string'
        && err.url.indexOf('/lead_manager/submit') !== -1;
      const isConsentSubmitError = err instanceof HttpErrorResponse
        && typeof err.url === 'string'
        && err.url.indexOf('/selected_manager/onetoone') !== -1;
      if (err instanceof HttpErrorResponse && !isLeadSubmitError) {
        if (isConsentSubmitError) {
          delete event.user;
        } else {
          // Drop other HttpErrorResponse events to prevent duplicate/unreadable Sentry events from Zone.js instrumentation.
          // Lead-submit errors are explicitly captured by rates-form so they remain observable.
          return null;
        }
      }
      const trackerBlockerDetails = getProbableTrackerBlockerDetails(event, hint);
      if (trackerBlockerDetails) {
        event = annotateProbableTrackerBlocker(event, trackerBlockerDetails);
      }
      return annotateRateFinderSentryEvent(event);
    },
  });

  const earlierStalls = (window as any).__hopeBootStalls;
  if (Array.isArray(earlierStalls)) {
    earlierStalls.forEach((detail: any) => captureHopeBootStall(detail));
  } else {
    captureHopeBootStall((window as any).__hopeBootStalled);
  }
  window.addEventListener('hopeBootStalled', (event: Event) => {
    captureHopeBootStall((event as CustomEvent).detail);
  });
}

watchHopeBootOutcome();

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() { }
  handleError(error: any) {
    const isLeadSubmitError = error instanceof HttpErrorResponse
        && typeof error.url === 'string'
        && error.url.indexOf('/lead_manager/submit') !== -1;
    if (error instanceof HttpErrorResponse && error.status === 0 && !isLeadSubmitError) {
      return; // suppress network failures (user connectivity, not app bugs)
    }
    if(sentryEnabled) {
      const exception = error.originalError || error.error || error.message || error;
      Sentry.captureException(exception instanceof Error ? exception : new Error(this.getErrorMessage(exception)));
    }
    this.reportHopeJsError(error);
    console.error(error);
  }

  private reportHopeJsError(error: any) {
    try {
      const message = this.getErrorMessage(error && (error.originalError || error.error || error.message || error));
      const w: any = window;
      if (!hopeJsErrorAllowed(message, w)) { return; }   // FRU-23403: one capped budget per page
      (w.__hopeEvents = w.__hopeEvents || []).push({
        kind: 'js_error',
        message,
        source: 'angular_error_handler',
        ts: new Date().toISOString()
      });
      const _paq = w._paq;
      if (_paq) {
        _paq.push(['trackEvent', 'Hope', 'js_error', message.slice(0, 80)]);
      }
    } catch (e) { /* non-fatal telemetry */ }
  }

  private getErrorMessage(exception): string {
    if (typeof exception === 'string') {
      return exception;
    }

    if (exception && exception.message) {
      return exception.message;
    }

    return 'Non-Error exception captured';
  }
}


@NgModule({ declarations: [
        AppComponent,
        BuyWithin90Component,
        BuyWithin90FormComponent,
        EmailVerificationComponent,
        LeadFormComponent,
        FormLandingComponent,
        FormLandingHeaderComponent,
        FormLandingHeaderDirective,
        FormLandingFooterComponent,
        FormLandingFooterDirective,
        FormLandingBodyComponent,
        FormLandingBodyDirective,
        LenderListingComponent,
        LenderListingsComponent,
        TrackingCodeComponent,
        MMLandingBodyComponent,
        MMLandingFooterComponent,
        MMLandingHeaderComponent,
        BestLandingBodyComponent,
        BestLandingFooterComponent,
        BestLandingHeaderComponent,
        ScriptTagComponent,
        RatesFormComponent,
        StepComponent,
        PropertyTypeComponent,
        PropertyUseComponent,
        DscrPropertyUseComponent,
        ReverseAgeComponent,
        ReverseBalanceComponent,
        ReverseResidenceComponent,
        ButtonStepComponent,
        BankruptcyComponent,
        BankruptcyRecencyComponent,
        EmploymentComponent,
        LoanReasonComponent,
        CashOutComponent,
        CashOutReasonComponent,
        SliderStepComponent,
        PropertyValueComponent,
        FirstMortgageBalanceComponent,
        CurrentFHAComponent,
        CreditRatingComponent,
        MultiInputStepComponent,
        SelectStepComponent,
        ZipCodeStreetAddressComponent,
        VeteranMilitaryComponent,
        CurrentVAComponent,
        FirstNameLastNameComponent,
        InputStepComponent,
        EmailComponent,
        PhoneComponent,
        BuyingProcessComponent,
        NewHomeValueComponent,
        EstimatedDownPaymentComponent,
        DscrEstimatedDownPaymentComponent,
        ZipCodeComponent,
        SliderLabelComponent,
        FormFlowShellComponent,
        BuyingProcessComponent,
        FirstTimeBuyerComponent,
        DaysFromBuyingComponent,
        FoundHomeComponent,
        SignedAgreementComponent,
        PremiumQuestionsComponent,
        PremiumLeadFormComponent,
        MaximumHomeEquityComponent,
        HaveExistingMortgageComponent,
        LoanPurposeComponent,
        NeustarComponent,
        ErrorSrcDirective,
        CustomerReviewComponent,
        DigitOnlyDirective,
        AutofillSyncDirective,
        SliderProgressDirective,
        TimeFrameComponent,
        PropertyStatusComponent,
        LinkClickModalComponent,
        SlideEntryComponent,
        CustomStarRatingComponent,
        StatesDropdownComponent,
        PropertyTypeConfirmationComponent,
        ProgressBarComponent,
        StateRateInfoComponent,
        TrustedPartnersComponent,
        TrustedPartnerComponent,
        ProductOptionsComponent,
        ProductInfoComponent,
        ProductOptionComponent,
        OneToOneConsentComponent,
        ConsentFormComponent,
        LendersCountComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        IMaskModule,
        AppRoutingModule], providers: [
        CommunicationService,
        FruHttpService,
        CookieService,
        BestService,
        [{ provide: ErrorHandler, useClass: SentryErrorHandler }],
        [{ provide: APP_BASE_HREF, useValue: '/communication/' }],
        { provide: APP_INITIALIZER, useFactory: warmAdvertisedRate, deps: [FruHttpService, BrandService], multi: true },
        provideHttpClient(withInterceptorsFromDi()),
        // FRU-25460: while Hope's chat is off, a plain XMLHttpRequest (as Angular's own BrowserXhr) and every request
        // passed straight through.
        FetchBackend,
        { provide: XhrFactory, useClass: XhrFetchFallbackXhrFactory },
        { provide: HTTP_INTERCEPTORS, useClass: XhrFetchFallbackInterceptor, multi: true }
    ] })
export class AppModule { }
